import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  executionService,
  ExecutionFunctionSummary,
  ExecutionHistoryItem,
  ExecutionStatus,
  ExecutionFunctionList,
  ExecutionHistoryList,
} from '../../services/execution';

interface ExecutionState {
  catalog: ExecutionFunctionSummary[];
  catalogLoading: boolean;
  catalogError: string | null;
  catalogLimit: number;
  catalogOffset: number;
  catalogTotal: number;

  history: ExecutionHistoryItem[];
  historyLoading: boolean;
  historyError: string | null;
  historyLimit: number;
  historyOffset: number;
  historyTotal: number;
  historyFilters: {
    rufid?: string;
    status?: ExecutionStatus;
    startedAfter?: string;
    startedBefore?: string;
  };
}

const initialState: ExecutionState = {
  catalog: [],
  catalogLoading: false,
  catalogError: null,
  catalogLimit: 25,
  catalogOffset: 0,
  catalogTotal: 0,

  history: [],
  historyLoading: false,
  historyError: null,
  historyLimit: 50,
  historyOffset: 0,
  historyTotal: 0,
  historyFilters: {},
};

export const fetchExecutionCatalog = createAsyncThunk<
  ExecutionFunctionList,
  { limit?: number; offset?: number } | undefined,
  { rejectValue: string }
>(
  'execution/fetchCatalog',
  async (
    params: { limit?: number; offset?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await executionService.listFunctions(params);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error(response.error?.message ?? 'Failed to load execution catalog');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load execution catalog');
    }
  }
);

interface ExecutionHistoryPayload extends ExecutionHistoryList {
  filters: ExecutionState['historyFilters'];
}

export const fetchExecutionHistory = createAsyncThunk<
  ExecutionHistoryPayload,
  {
    limit?: number;
    offset?: number;
    rufid?: string;
    status?: ExecutionStatus;
    startedAfter?: string;
    startedBefore?: string;
  } | undefined,
  { rejectValue: string }
>(
  'execution/fetchHistory',
  async (
    params = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await executionService.listHistory(params);
      if (response.status === 'success' && response.data) {
        return {
          ...response.data,
          filters: {
            rufid: params.rufid,
            status: params.status,
            startedAfter: params.startedAfter,
            startedBefore: params.startedBefore,
          },
        };
      }
      throw new Error(response.error?.message ?? 'Failed to load execution history');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load execution history');
    }
  }
);

const executionSlice = createSlice({
  name: 'execution',
  initialState,
  reducers: {
    setHistoryFilters(state, action: PayloadAction<ExecutionState['historyFilters']>) {
      state.historyFilters = action.payload;
    },
    resetHistory(state) {
      state.history = [];
      state.historyOffset = 0;
      state.historyTotal = 0;
      state.historyError = null;
      state.historyFilters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutionCatalog.pending, (state) => {
        state.catalogLoading = true;
        state.catalogError = null;
      })
      .addCase(fetchExecutionCatalog.fulfilled, (state, action) => {
        state.catalogLoading = false;
        state.catalog = action.payload.offset > 0
          ? [...state.catalog, ...action.payload.items]
          : action.payload.items;
        state.catalogLimit = action.payload.limit;
        state.catalogOffset = action.payload.offset;
        state.catalogTotal = action.payload.total;
      })
      .addCase(fetchExecutionCatalog.rejected, (state, action) => {
        state.catalogLoading = false;
        state.catalogError = (action.payload as string) ?? 'Failed to load execution catalog';
      });

    builder
      .addCase(fetchExecutionHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchExecutionHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload.offset > 0
          ? [...state.history, ...action.payload.items]
          : action.payload.items;
        state.historyLimit = action.payload.limit;
        state.historyOffset = action.payload.offset;
        state.historyTotal = action.payload.total;
        state.historyFilters = action.payload.filters;
      })
      .addCase(fetchExecutionHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = (action.payload as string) ?? 'Failed to load execution history';
      });
  },
});

export const { setHistoryFilters, resetHistory } = executionSlice.actions;
export default executionSlice.reducer;
